import {FormEventHandler, useRef, useState} from 'react';
import InputError from '@/Components/InputError';
import InputLabel from '@/Components/InputLabel';
import Modal from '@/Components/Modal';
import PrimaryButton from '@/Components/PrimaryButton';
import SecondaryButton from '@/Components/SecondaryButton';
import TextInput from '@/Components/TextInput';
import {useForm} from '@inertiajs/react';
import toast from "react-hot-toast";

export default function SetPasswordForm({status, className = ''}: {
    status?: string,
    className?: string
}) {
    const [showModal, setShowModal] = useState(false);
    const passwordInput = useRef<HTMLInputElement>();

    const {data, setData, post, errors, processing, reset} = useForm({
        password: '',
        password_confirmation: '',
    });

    const submit: FormEventHandler = (e) => {
        e.preventDefault();

        post(route('profile.set-password'), {
            preserveScroll: false,
            onSuccess: () => {
                closeModal();
                toast.success('Password set successfully.');
            },
            onError: () => passwordInput.current?.focus(),
        });
    };

    const closeModal = () => {
        setShowModal(false);
        reset();
    };

    return (
        <section className={className}>
            <header>
                <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Set Password</h2>

                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Set a password to sign in with your email address.
                </p>
            </header>

            {status === 'password-set' && (
                <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-md">
                    <p className="text-sm text-green-800 dark:text-green-200">
                        Your password has been set. You can now sign in with your email and password.
                    </p>
                </div>
            )}

            <PrimaryButton className="mt-6" onClick={() => setShowModal(true)}>Set Password</PrimaryButton>

            <Modal show={showModal} onClose={closeModal} maxWidth="md">
                <form onSubmit={submit} className="p-6">
                    <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                        Set a Password
                    </h2>

                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        Choose a password to sign in with your email address.
                    </p>

                    <div className="mt-6">
                        <InputLabel htmlFor="set_password" value="New Password"/>

                        <TextInput
                            id="set_password"
                            ref={passwordInput}
                            type="password"
                            className="mt-1 block w-full"
                            value={data.password}
                            onChange={(e) => setData('password', e.target.value)}
                            autoComplete="new-password"
                            isFocused
                        />

                        <InputError className="mt-2" message={errors.password}/>
                    </div>

                    <div className="mt-4">
                        <InputLabel htmlFor="set_password_confirmation" value="Confirm Password"/>

                        <TextInput
                            id="set_password_confirmation"
                            type="password"
                            className="mt-1 block w-full"
                            value={data.password_confirmation}
                            onChange={(e) => setData('password_confirmation', e.target.value)}
                            autoComplete="new-password"
                        />

                        <InputError className="mt-2" message={errors.password_confirmation}/>
                    </div>

                    <div className="mt-6 flex justify-end">
                        <SecondaryButton className="me-2" onClick={closeModal}>Cancel</SecondaryButton>

                        <PrimaryButton disabled={processing}>
                            Set Password
                        </PrimaryButton>
                    </div>
                </form>
            </Modal>
        </section>
    );
}
